import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface AdminAuthContextType {
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  getAuthHeader: () => string;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const AdminAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  useEffect(() => {
    // Check if already logged in
    const authData = localStorage.getItem('culturalEventsAdminAuth');
    if (authData) {
      setIsAuthenticated(true);
    }
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      // Create the auth string
      const authString = `${username}:${password}`;
      
      // Test the credentials by making a request to backend
      // Force production API URL (override any env vars for now)
      const API_BASE_URL = 'https://cultural-events-api-80b5fb86b6c5.herokuapp.com/api';
      
      console.log('🔐 Attempting admin login to:', `${API_BASE_URL}/admin/cultural-events`);
      console.log('🔍 Env var REACT_APP_CULTURAL_EVENTS_API:', process.env.REACT_APP_CULTURAL_EVENTS_API);
      
      const response = await axios.get(`${API_BASE_URL}/admin/cultural-events`, {
        headers: {
          'Authorization': `Admin ${authString}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        timeout: 15000
      });

      if (response.status === 200 || response.status === 201) {
        localStorage.setItem('culturalEventsAdminAuth', authString);
        setIsAuthenticated(true);
        console.log('✅ Admin login successful');
        return true;
      } else {
        console.error('❌ Admin login failed:', response.status);
        return false;
      }
    } catch (error: any) {
      console.error('❌ Admin login error:', error);
      console.error('❌ Error details:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('culturalEventsAdminAuth');
    setIsAuthenticated(false);
    console.log('👋 Admin logged out');
  };

  const getAuthHeader = (): string => {
    const authData = localStorage.getItem('culturalEventsAdminAuth');
    return authData ? `Admin ${authData}` : '';
  };

  return (
    <AdminAuthContext.Provider value={{ isAuthenticated, login, logout, getAuthHeader }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

